// Sidebar nav — legacy stbarts pattern. Groups are derived from the target's
// path (output dir) split into "front/en/common" / "front/en/pages" etc, and
// the displayed label is the legacy path (e.g. "/en", "/en/stay/villas") or
// the slug short name for menu/footer. 1 badge per item, prioritised:
//   - statusCounts.open (color blue)  if > 0
//   - statusCounts.blocked (red)     if > 0
//   - statusCounts.pending (orange)  if > 0
//   - capture count (grey) otherwise

import { For, Show, createMemo } from 'solid-js';
import { actions, stores } from '../state/store';
import type { TargetWithCounts } from '../state/store';

interface TargetEnriched extends TargetWithCounts {
  group: string;
  label: string;
}

// Legacy TARGET_PATHS (index.html L2176-2188) — map slug → {group, label}.
// Slugs not listed fall back to a path-derived heuristic so the schema
// extends to new workspaces without code changes.
const LEGACY_OVERRIDES: Record<string, { group: string; label: string }> = {
  'front:menu':                                  { group: 'front/en/common', label: 'menu' },
  'front:footer':                                { group: 'front/en/common', label: 'footer' },
  'front:homepage':                              { group: 'front/en/pages',  label: '/en' },
  'front:villas':                                { group: 'front/en/pages',  label: '/en/stay/villas' },
  'front:villas-slug':                           { group: 'front/en/pages',  label: '/en/stay/villas/[slug]' },
  'front:villas-slug-photos':                    { group: 'front/en/pages',  label: '/en/stay/villas/[slug]/photos' },
  'front:villas-slug-photos-zoomed':             { group: 'front/en/pages',  label: '/en/stay/villas/[slug]/photos (zoomed)' },
  'front:villas-locations':                      { group: 'front/en/pages',  label: '/en/stay/villas/locations' },
  'front:villas-locations-slug':                 { group: 'front/en/pages',  label: '/en/stay/villas/locations/[slug]' },
  'front:villas-collections':                    { group: 'front/en/pages',  label: '/en/stay/villas/collections' },
  'front:villas-collections-slug':               { group: 'front/en/pages',  label: '/en/stay/villas/collections/[slug]' },
};

function deriveGroupLabel(t: TargetWithCounts): { group: string; label: string } {
  const overr = LEGACY_OVERRIDES[t.slug];
  if (overr) return overr;
  // Generic: group = parent path, label = leaf slug
  const parts = t.path.split('/');
  return {
    group: parts.slice(0, -1).join('/') || 'front',
    label: parts[parts.length - 1] ?? t.slug,
  };
}

function statusCounts(targetSlug: string) {
  let open = 0, blocked = 0, pending = 0;
  for (const r of stores.state.remarks) {
    if (r.targetSlug !== targetSlug) continue;
    if (r.status === 'open' || r.status === 'todo') open++;
    else if (r.status === 'blocked' || r.status === 'wontfix') blocked++;
    else if (r.status === 'pending' || r.status === 'doing') pending++;
  }
  return { open, blocked, pending };
}

export function TargetsSidebar() {
  const enriched = createMemo<TargetEnriched[]>(() =>
    stores.state.targets.map((t) => ({ ...t, ...deriveGroupLabel(t) })),
  );

  // Group by `group`. Order = legacy TARGET_PATHS sequence: common first,
  // then pages. Generic fallback for unknown groups is alphabetical.
  const GROUP_ORDER = ['front/en/common', 'front/en/pages'];
  const grouped = createMemo<Array<[string, TargetEnriched[]]>>(() => {
    const map = new Map<string, TargetEnriched[]>();
    for (const t of enriched()) {
      if (!map.has(t.group)) map.set(t.group, []);
      map.get(t.group)!.push(t);
    }
    return [...map.entries()].sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a[0]);
      const bi = GROUP_ORDER.indexOf(b[0]);
      if (ai === -1 && bi === -1) return a[0].localeCompare(b[0]);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  });

  return (
    <Show
      when={stores.state.activeProjectId}
      fallback={<p class="empty">Pick a project</p>}
    >
      <Show
        when={stores.state.targets.length > 0}
        fallback={<p class="empty">Loading…</p>}
      >
        <For each={grouped()}>
          {([groupPath, items]) => (
            <Show when={items.length > 0}>
              <div class="nav-group">
                <div class="nav-group-title">
                  {groupPath.toUpperCase().replace(/\//g, ' / ')}
                </div>
                <For each={items}>
                  {(t) => {
                    const sc = createMemo(() => statusCounts(t.slug));
                    return (
                      <div
                        class={`nav-item ${stores.state.activeTargetId === t.id ? 'active' : ''} ${t.capturesCount === 0 ? 'disabled' : ''}`}
                        data-path={t.path}
                        title={t.path}
                        style={t.capturesCount === 0 ? 'opacity:0.4;pointer-events:none' : ''}
                        onClick={() => void actions.selectTarget(t.id)}
                      >
                        <span>{t.label}</span>
                        <div class="nav-badges">
                          <Show
                            when={sc().open > 0}
                            fallback={
                              <Show
                                when={sc().blocked > 0}
                                fallback={
                                  <Show
                                    when={sc().pending > 0}
                                    fallback={
                                      // Legacy parity: show capture count only
                                      // when target has captures AND no status
                                      // counts. Targets with 0 captures stay
                                      // badge-less (disabled, greyed out).
                                      <Show when={t.capturesCount > 0}>
                                        <span class="badge">{t.capturesCount}</span>
                                      </Show>
                                    }
                                  >
                                    <span class="badge status-pending">{sc().pending}</span>
                                  </Show>
                                }
                              >
                                <span class="badge status-blocked">{sc().blocked}</span>
                              </Show>
                            }
                          >
                            <span class="badge status-open">{sc().open}</span>
                          </Show>
                        </div>
                      </div>
                    );
                  }}
                </For>
              </div>
            </Show>
          )}
        </For>
      </Show>
    </Show>
  );
}
